import SEO from "../../components/SEO";
import ScrollDepthTracker from "../../components/ScrollDepthTracker";
import SeriesNavigation from "../../components/SeriesNavigation";
import { clinicalTrials } from "../../data/navigationData";

export default function CoSTARS() {
  const CoSTARSSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "CoSTARS",
    "url": "https://www.thrombio.com.au/trials/CoSTARS",
    "mainEntity": {
      "@type": "Organization",
      "name": "Thrombio",
      "url": "https://www.thrombio.com.au/",
      "logo": "https://www.thrombio.com.au/thromBioInvert.png",
      "contactPoint": {
        "@type": "ContactPoint",
        "email": "info@thrombio.com.au"
      },
      "sameAs": [
        "https://www.linkedin.com/company/thrombioholdings"
      ]
    },
    "about": {
      "@type": "MedicalEntity",
      "name": "TBO-309",
      "description": "TBO-309 is a novel antiplatelet compound currently in Phase II clinical trials for the treatment of stroke with tandem occlusion."
    }
  };

  return (
    <>
      <SEO
        title="CoSTARS"
        description="Dive into our CoSTARS clinical trial examining TBO-309's safety, tolerability and efficacy in reperfusion for stroke with tandem occlusion"
        keywords={['Tandem Large Vessel Occlusion', 'Tandem-LVO', 'Clinical Trials', 'CoSTARS', 'Blood Clots', 'Stroke Treatment', 'Carotid Stenting', 'Embolic stroke', 'Stroke', 'Stent thrombosis', 'Embolism', 'Novel therapy', 'Anti-platelet', 'Platelet inhibitor']}
        ogImage="/costarslogo.webp"
        schema={CoSTARSSchema}
        clinicalTrial={true}
        drugInformation={true}
      />
      <ScrollDepthTracker pageCategory="trials" pageName="costars" />

      <div className="xl:page-width px-12 pt-[80px] md:pt-44 text-[2.1rem] leading-[3.1rem] tracking-[.042rem]">

        {/* Header Section with Logo and Title */}
        <div className="flex flex-col mb-[120px]">
          {/* Mobile Layout */}
          <div className="md:hidden flex flex-col items-center">
            <div className="text-white text-[4.2rem] font-bold leading-[52px] tracking-[0.141rem] [text-shadow:_0px_4px_9px_rgb(0_0_0_/_1.00)] text-center">
              Clinical Trials
            </div>
            <div className="text-[#DA0912] text-[5rem] font-bold leading-[52px] tracking-[0.141rem] [text-shadow:_0px_4px_9px_rgb(0_0_0_/_1.00)] font-syne text-center mb-12">
              CoSTARS
            </div>

            <div className="mb-8">
              <img src="/costarslogo.webp" alt="CoSTARS Clinical Trial Logo" className="h-[325px] w-auto" />
            </div>

            <div className="flex flex-col text-left text-[2.2rem] md:text-[2.8rem] leading-[3.6rem] tracking-[.08rem] text-[#E0FFFF] font-syne font-light w-full max-w-[300px]">
              <div><span className="font-bold text-[2.7rem]">S</span>afety</div>
              <div><span className="font-bold text-[2.7rem]">T</span>olerability and efficacy of</div>
              <div><span className="font-bold text-[2.7rem]">A</span>djunctive TBO-309 in</div>
              <div><span className="font-bold text-[2.7rem]">R</span>eperfusion for</div>
              <div className="leading-[1]"><span className="font-bold text-[2.7rem]">S</span>troke with tandem occlusion</div>
            </div>
          </div>

          {/* Desktop/Tablet Layout */}
          <div className="hidden md:block">
            <div className="flex items-center gap-16 mb-12">
              <div className="text-white text-[3.6rem] md:text-[4.5rem] font-bold leading-[52px] tracking-[0.141rem] [text-shadow:_0px_4px_9px_rgb(0_0_0_/_1.00)]">
                Clinical Trials
              </div>
              <div className="text-[#DA0912] text-[6rem] md:text-[7.5rem] font-bold leading-[52px] tracking-[0.141rem] [text-shadow:_0px_4px_9px_rgb(0_0_0_/_1.00)] font-syne">
                CoSTARS
              </div>
            </div>

            <div className="text-white text-[2.4rem] md:text-[2.8rem] leading-[3.2rem] tracking-[.08rem] mb-8">
              Co-STARS Phase IIa Clinical Trial
            </div>

            <div className="flex items-end gap-16 w-full max-w-[120ch]">
              <div className="flex-shrink-0">
                <img src="/costarslogo.webp" alt="CoSTARS Clinical Trial Logo" className="h-[325px] w-auto" />
              </div>

              <div className="flex flex-col text-left text-[2.4rem] md:text-[2.8rem] leading-[3.6rem] tracking-[.08rem] text-[#E0FFFF] font-syne font-light">
                <div><span className="font-bold">S</span>afety</div>
                <div><span className="font-bold">T</span>olerability and efficacy of</div>
                <div><span className="font-bold">A</span>djunctive TBO-309 in</div>
                <div><span className="font-bold">R</span>eperfusion for</div>
                <div><span className="font-bold">S</span>troke with tandem occlusion</div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col max-w-[80ch] mt-[120px] md:mt-[90px]">
          <div className="pb-[90px] md:pt-[24px]">
            <div className="pb-[10px]">Co-STARS is an active Phase II clinical trial testing whether a new drug, TBO-309, can make emergency stroke surgery safer and more effective for patients with a "tandem occlusion" stroke — a severe type of stroke where arteries in both the neck and the brain are blocked at the same time, simultaneously starving the brain of oxygen from two points.</div>
            <div className="pb-[10px]">Around 46,000 Australians have a stroke each year, and tandem occlusion patients face some of the highest rates of death and permanent disability.</div>
            <div className="pb-[10px]">Current emergency treatment involves physically removing both clots and inserting a stent — a small metal scaffold — to prevent blockade of the diseased artery. A major challenge with stents is their ability to trigger fresh clot formation, necessitating the use of blood-thinning drugs that can increase the risk of fatal bleeding in the brain. As a result, there is a pressing need to identify safer blood thinning drugs that can prevent stent clotting without increasing bleeding risk.</div>
            <div className="pb-[10px]">The unprecedented safety profile of TBO-309 in human trials makes this an exciting candidate to prevent stent thrombosis. After 30 years of research, TBO-309 has been proven to block the function of a single protein inside platelets, called PI 3-kinase-beta (PI3Kβ). ThromBio’s scientists were the first group to identify the critical role PI3Kβ played in promoting disease-causing blood clots in damaged arteries, but is less important for the body's normal, healthy response to vessel damage and bleeding. By selectively switching off PI3Kβ, TBO-309 aims to prevent dangerous re-clotting on the stent without triggering brain bleeds.</div>
          </div>
        </div>

        <div className="flex flex-col gap-8 max-w-[80ch] mb-[90px]">
          <div className="text-[#E0FFFF] max-md:text-center md:w-full font-syne text-[3.3rem] font-bold leading-[3.2rem] tracking-[.165rem]">
            Tandem Large Vessel Occlusion (Tandem-LVO) Strokes
          </div>
          <div className="pt-[24px]">
            A distinct, and very important subset of strokes, are the Tandem-LVO strokes, which constitute ~20-30% of all LVO strokes.These are amongst the most severe forms of strokes, which commonly lead to death or severe long-term disability if not treated promptly. With Tandem-LVO, two separate arteries are blocked in tandem, both affecting blood supply to the brain. The optimal treatment strategy for Tandem-LVO stroke patients is not clear, and as a result, there is not an optimised, standard treatment protocol for this disease.
          </div>
        </div>

        <div className="flex flex-col gap-8 max-w-[80ch] mb-[90px]">
          <div className="text-[#DA0912] font-syne text-[2.4rem] font-bold leading-[3.6rem] tracking-[.12rem]">
            What is a Tandem LVO stroke?
          </div>
          <div>
            Tandem occlusions are defined as blockages of both an extracranial (an artery in the neck) and an intracranial artery (an artery inside the brain). More specifically:
          </div>
          <div className="flex flex-col gap-6 pl-8">
            <div className="flex gap-4">
              <span className="font-bold">1.</span>
              <div>A "high-grade" blockage is often due to a clot forming on an atherosclerotic plaque in the internal carotid artery, and</div>
            </div>
            <div className="flex gap-4">
              <span className="font-bold">2.</span>
              <div>Part of the clot in the carotid artery breaks off and lodges downstream in an intracranial artery, such as the middle cerebral artery.</div>
            </div>
          </div>
          <div className="pt-4">
            This dual blockage of the arterial blood supply to the brain often leads to a large area of brain damage, if timely intervention to unblock these arteries is not achieved.
          </div>
        </div>

        <div className="flex flex-col gap-8 max-w-[80ch] mb-[90px]">
          <div className="text-[#DA0912] font-syne text-[2.4rem] font-bold leading-[3.6rem] tracking-[.12rem]">
            How is a Tandem-LVO treated?
          </div>
          <div>
            It is widely accepted that administration of intravenous thrombolysis (IVT) as a sole treatment for LVO strokes (those without Tandem Occlusion) is associated with a poor functional outcome in up to 80% of patients. This is typically due to the large size of the clot, which are notoriously difficult to dissolve with thrombolytic therapies alone.
          </div>
          <div>
            Unfortunately, while mechanical thrombectomy and clot-retrieval is considered the 'gold-standard treatment' for most LVO strokes, performing this procedure on patients with a tandem occlusion stroke is more complex, as it necessitates the treatment of two separate arterial blockages. This introduces a series of other complications that can significantly impact patient outcome, including:
          </div>
          <div className="flex flex-col gap-4 pl-8">
            <div className="flex gap-4">
              <span className="font-bold">1.</span>
              <div>complicated intracranial access that can delay recanalisation;</div>
            </div>
            <div className="flex gap-4">
              <span className="font-bold">2.</span>
              <div>manipulation of the atherosclerotic lesion that could cause secondary clot embolisation if part of the clot breaks off and lodges downstream</div>
            </div>
            <div className="flex gap-4">
              <span className="font-bold">3.</span>
              <div>additional treatments of the tandem lesion during thrombectomy might be required, including the deployment of a carotid stent to prevent arterial reocclusion. Patients receiving carotid stenting require additional antiplatelet medications to prevent stent occlusion; however, <span className="underline">currently approved</span> antiplatelet medications <span className="underline">increase the risk of intracranial haemorrhage</span>, especially in patients who have had a severe stroke or have received other blood thinning agents.</div>
            </div>
          </div>
          <div className="pt-4">
            The management of tandem occlusion strokes is fraught with dangers and is one of the most challenging subtypes of stroke to treat, with an unacceptably high bleeding rate, that leads to poor patient outcomes.
          </div>
        </div>

        <div className="flex flex-col gap-8 max-w-[80ch] mb-[90px]">
          <div className="text-[#DA0912] font-syne text-[2.4rem] font-bold leading-[3.6rem] tracking-[.12rem]">
            What is the aim of the Co-STARS trial?
          </div>
          <div>
            Given the unacceptably high bleeding rate in tandem occlusion strokes when patients are administered a combination of anti-clotting agents, there is an urgent need to identify safer, more effective antiplatelet drugs that can improve vessel patency and reduce in-stent thrombosis without exacerbating bleeding risk.
          </div>
          <div>
            The <span className="font-bold">CoSTARS</span> Study will examine the: <span className="font-bold">Safety, Tolerability and efficacy of Adjunctive TBO-309 in Reperfusion for Stroke with tandem occlusion</span>. This proof-of-concept Phase IIa trial aims to establish the optimal clinical dose of TBO-309 in the setting of Tandem Large Vessel Occlusions (LVO).
          </div>
          <div>
            <span className="font-bold">Study Intervention:</span> Patients with tandem occlusion undergoing carotid artery stenting will receive intravenous aspirin and TBO-309. Rescue GPIIb/IIIa inhibitors may be used to reverse in-stent thrombosis during the procedure.
          </div>
          <div>
            Two intervention doses will be tested sequentially for TBO-309, with a maximum of 78 patients to be recruited across two intervention doses in this study.
          </div>
        </div>

        <div className="flex flex-col md:max-xl:grid items-center md:max-xl:items-start gap-10 md:gap-2 xl:gap-12 md:px-24 mb-[90px]">
          <div className="flex flex-col md:max-xl:grid grid-cols-2 gap-2 items-center md:font-normal">
            <div>Clinical Trials ID:</div>
            <div className="text-[3.4rem] md:text-[2.5rem] xl:text-[3.6rem] font-syne leading-[3.9rem] tracking-[.034rem] md:tracking-[.125rem] xl:tracking-[.03rem]">
              NCT05363397
            </div>
          </div>
          <div className="flex flex-col md:max-xl:grid grid-cols-2 gap-4 items-center md:font-normal">
            <div>Study Details:</div>
            <a href="https://clinicaltrials.gov" target="_blank" rel="noopener noreferrer" className="text-[2.8rem] md:text-[2.2rem] xl:text-[2.8rem] font-syne leading-[3.6rem] tracking-[.034rem] md:tracking-[.125rem] xl:tracking-[.03rem] underline text-[#C3EEFD]">
              Safety, Tolerability and Efficacy of Adjunctive TBO-309 in Reperfusion for Stroke With Tandem Occlusion | ClinicalTrials.gov
            </a>
          </div>
          <div className="flex flex-col md:max-xl:grid grid-cols-2 gap-4 items-center md:font-normal">
            <div>Sponsor:</div>
            <div className="text-[3.4rem] md:text-[2.5rem] xl:text-[3.6rem] font-syne leading-[3.9rem] tracking-[.034rem] md:tracking-[.125rem] xl:tracking-[.03rem]">
              ThromBio Holdings Pty. Ltd.
            </div>
          </div>
          <div className="flex flex-col md:max-xl:grid grid-cols-2 gap-4 items-center md:font-normal">
            <div>Collaborator:</div>
            <div className="text-[3.4rem] md:text-[2.5rem] xl:text-[3.6rem] font-syne leading-[3.9rem] tracking-[.034rem] md:tracking-[.125rem] xl:tracking-[.03rem]">
              Global Clinical Innovation Group Pty Ltd
            </div>
          </div>
          <div className="flex flex-col md:max-xl:grid grid-cols-2 gap-4 items-center md:font-normal">
            <div>Recruiting:</div>
            <div className="font-syne text-[3rem] md:text-[2.5rem] xl:text-[3.6rem] leading-[2.7rem] md:leading-[3.4rem] tracking-[.03rem] md:tracking-[.125rem] shadow-[0_4px_7.9px_#000] xl:tracking-[.03rem]">
              Currently Recruiting
            </div>
          </div>
        </div>

        <div className="pt-[75px] flex flex-col md:items-center gap-[75px] md:mb-20">
          <div className="max-md:gap-[75px] md:flex justify-evenly items-end w-full">
            <div className="flex flex-col gap-8">
              <div className="flex flex-col items-center">
                <div className="w-[300px] pb-10 flex justify-center">
                  <div className="text-center text-[3.4rem] md:text-[3.5rem] xl:text-[4rem] leading-[3.9rem] tracking-[.034rem] md:tracking-[.125rem] xl:tracking-[.03rem] font-bold text-[#E0FFFF]">
                    John Hunter Hospital
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-[1.8rem] leading-[2.5rem] tracking-[.09rem]">Principle Investigator</div>
                  <div className="text-[2.1rem] leading-[3.2rem] tracking-[.063rem]">Ferdinand Miteff</div>
                  <div className="text-[1.6rem] leading-[2.4rem] tracking-[.048rem] text-[#C3EEFD]">Interventional Neurologist, RACR CCINR</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <SeriesNavigation
        items={clinicalTrials}
        currentSlug="costars"
        basePath="/trials"
      />
    </>
  );
}